/**
 * Phase 8 T-06 §7 B–K — Smart Match three-column canvas layout.
 *
 * This component owns:
 *   - Tab state (Sourcing / Shortlisted / Hidden) + per-match local bucketing.
 *   - Focused card keyboard nav (`j`/`k`/`s`/`h`/Enter).
 *   - Shortlist → `ensureApplicationForMatch` (creates pipeline row) — the
 *     existing Phase-7 automation hooks fire exactly where they did before.
 *   - Hide → local-session filter; persistence is `TODO(§8-v2)` since the
 *     matchResult schema has no hidden flag and this ticket is frozen to
 *     existing services.
 *   - Responsive grid: `360px 1fr 360px` xl+, `320px 1fr` on lg (right rail
 *     becomes a Drawer), single column on md-.
 *   - Wires the right rail's "Ask about this candidate…" input into the
 *     left-column agent thread via an imperative handle.
 *
 * Per the ticket we do **not** touch any file in `src/modules/orchestrator/`
 * or `src/modules/agent/` or any Supabase service — only reads of existing
 * ones.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  X,
  CheckCircle2,
  AlertTriangle,
  FolderOpen,
  CheckSquare,
  ChevronsLeft,
  ChevronsRight,
  Eye,
  EyeOff,
} from 'lucide-react';
import { useAuth } from '@core/auth/useAuth';
import { applicationService } from '@core/supabase/services/applicationService';
import { Drawer } from '@shared/components/ui/Drawer';
import type { AgentToolResult } from '@modules/agent/hooks/useAgentThread';
import type { SourcingResult } from '@modules/agent/tools/connectors/types';
import type { MatchResult } from '../types';
import { useMatchResults } from '../hooks/useMatchResults';
import { useSmartMatchSearch } from '../hooks/useSmartMatchSearch';
import { useAlternateJobsForCandidates } from '../hooks/useAlternateJobsForCandidates';
import { isPineconeBreakerOpen } from '../lib/promptSearch';
import { prettifyChannel } from '../lib/matchDisplay';
import { ensureApplicationForMatch } from '../lib/ensureMatchApplication';
import type { IntakeSource, PipelineIntake } from '../lib/matchCanvasPipeline';
import { writePipelineIntake } from '../lib/matchCanvasPipeline';
import type { ScopeId } from '@modules/agent/components/AgentScopePill';
import { AgentThreadColumn, type AgentThreadHandle } from './AgentThreadColumn';
import {
  INTAKE_GRADIENT_LIGHT,
  INTAKE_GRADIENT_DARK,
} from '@modules/agent/components/AssistantIntake';
import {
  MatchStackColumn,
  type CanvasTab,
  type CanvasGroupBy,
} from './MatchStackColumn';
import { CandidateContextRail } from './CandidateContextRail';
import {
  CanvasFilterPanel,
  DEFAULT_CANVAS_FILTERS,
  countActiveFilters,
  type CanvasFilters,
  type CanvasFilterOption,
} from './CanvasFilterPanel';
import {
  getThread,
  patchThread,
  getStage,
  setStage,
  forceSetStage,
  getClarificationRoundCount,
  incrementClarificationRound,
  markCancelledRemainder,
  type SmartMatchCollectionRef,
  type SmartMatchIntakeTab,
  type SmartMatchJdEntry,
  type SmartMatchMode,
  type SmartMatchResumeEntry,
  type SmartMatchStage,
} from '../lib/smartMatchThreads';
import { readPendingResumeTexts } from '../lib/pendingResumeTexts';
import { touchRecent } from '../lib/recentSearchChip';
import type { ManualCandidateInput } from './AddCandidateInlineRow';
import { ClarificationStage, type ClarificationRound } from './ClarificationStage';
import { NextStepBridge } from './NextStepBridge';
import { CollectionsPicker } from './CollectionsPicker';
import { CompareDrawer } from './CompareDrawer';
import { pinCollection } from '@shared/components/Layout/SidebarCollections';
import {
  buildBulkResumeManualSummary,
  buildBulkResumeParaphrase,
  buildFollowUpParaphraseLlm,
  buildInitialParaphraseLlm,
  buildMultiJdCombinedParaphrase,
  getParaphraseConfidence,
  stubParaphraseConfidence,
} from '../lib/paraphraseStub';
import { BulkComposePanel } from '@shared/components/BulkComposePanel';

interface MatchCanvasLayoutProps {
  pipelineId: string;
  intake: PipelineIntake;
  /**
   * Phase 8 · Addendum F §33 — `'streaming'` swaps the agent column onto
   * `agentRunnerService.streamReply`, which fans out to every configured
   * sourcing connector via `detectSourcingIntent` and renders results
   * through `AgentSourcingResults`. The Sourcing canvas
   * (`/sourcing/:id`) opts into this; Smart Match keeps the default
   * `'legacy'` T-06 tool-aware pipeline.
   */
  agentRunnerVariant?: 'legacy' | 'streaming';
  /**
   * Wrangle parity (§33, image 2). Optional React slot rendered at the
   * top of the agent column (above the Source header + Copilot card).
   * The Sourcing canvas passes its persistent History panel here so the
   * left column stacks History → Copilot → chat → composer in a single
   * column, instead of History being an external sibling column.
   */
  agentLeadingSlot?: React.ReactNode;
  /**
   * Post user-feedback: when provided, the top action bar (NextStepBridge)
   * renders a leading "+ New chat" button on every canvas state — empty
   * state AND results — so the affordance is never hidden inside a side
   * panel. Smart Match doesn't pass this (its intake lives on a separate
   * page); Sourcing wires it to `navigate('/sourcing/new')`.
   */
  onNewChat?: () => void;
}

interface Toast {
  id: string;
  tone: 'ok' | 'err';
  text: string;
  action?: {
    label: string;
    onClick: () => void;
  };
}

function matchKey(m: MatchResult): string {
  return `${m.candidate_id}-${m.job_id}`;
}

// ── Agent panel persistence (post user-feedback, 21-Apr) ──────────────────
//
// Users can hide the agent column and cycle through three widths so the
// middle column breathes when they want to scan lots of cards. Both
// settings persist in `localStorage` under dedicated keys.
const LS_AGENT_OPEN = 'ggj.canvas.agent-open';
const LS_AGENT_SIZE = 'ggj.canvas.agent-size';
type AgentSize = 'sm' | 'md' | 'lg';
const AGENT_SIZE_PX: Record<AgentSize, number> = { sm: 280, md: 360, lg: 480 };
const AGENT_SIZE_ORDER: AgentSize[] = ['sm', 'md', 'lg'];

function readAgentOpen(): boolean {
  try {
    const v = localStorage.getItem(LS_AGENT_OPEN);
    // Default to OPEN — first-time users still see the agent.
    return v === null ? true : v === '1';
  } catch {
    return true;
  }
}
function writeAgentOpen(open: boolean): void {
  try {
    localStorage.setItem(LS_AGENT_OPEN, open ? '1' : '0');
  } catch {
    /* ignore */
  }
}
function readAgentSize(): AgentSize {
  try {
    const v = localStorage.getItem(LS_AGENT_SIZE);
    if (v === 'sm' || v === 'md' || v === 'lg') return v;
  } catch {
    /* ignore */
  }
  return 'md';
}
function writeAgentSize(s: AgentSize): void {
  try {
    localStorage.setItem(LS_AGENT_SIZE, s);
  } catch {
    /* ignore */
  }
}

// ── Canvas view mode (list | grid) ─────────────────────────────────────────
//
// Post user-feedback (21-Apr): Sourcing & Smart Match users want to
// toggle between a dense grid (scan lots of candidates) and a list
// (reads one-per-row with more horizontal room for role + match tags).
// Persisted in `localStorage` so the choice survives reloads.
export type CanvasViewMode = 'grid' | 'list';
const LS_CANVAS_VIEW_MODE = 'ggj.canvas.view-mode';
function readCanvasViewMode(): CanvasViewMode {
  try {
    const v = localStorage.getItem(LS_CANVAS_VIEW_MODE);
    if (v === 'list' || v === 'grid') return v;
  } catch {
    /* ignore */
  }
  return 'grid';
}
function writeCanvasViewMode(v: CanvasViewMode): void {
  try {
    localStorage.setItem(LS_CANVAS_VIEW_MODE, v);
  } catch {
    /* ignore */
  }
}

// ── Canvas group-by (21-Apr audit gap) ────────────────────────────────────
//
// `none` = flat list (legacy). `client` = one section per client_id.
// `job` = one section per job_id. Persisted so the recruiter's
// preferred grouping survives reloads — especially useful for
// multi-JD runs where "by JD" is the default way to scan results.
const LS_CANVAS_GROUP_BY = 'ggj.canvas.group-by';
function readCanvasGroupBy(): CanvasGroupBy {
  try {
    const v = localStorage.getItem(LS_CANVAS_GROUP_BY);
    if (
      v === 'client' || v === 'job' || v === 'channel' || v === 'none' ||
      (typeof v === 'string' && v.startsWith('channel:'))
    ) return v as CanvasGroupBy;
  } catch {
    /* ignore */
  }
  return 'none';
}
function writeCanvasGroupBy(v: CanvasGroupBy): void {
  try {
    localStorage.setItem(LS_CANVAS_GROUP_BY, v);
  } catch {
    /* ignore */
  }
}

// ── Viewed/seen candidates + hide-seen toggle (post user-feedback) ────────
//
// The Shortlisted tab keeps a per-pipeline set of candidates the user has
// already glanced at ("Mark as seen"). Seen cards remain in the shortlist
// but gain a Seen badge and are hidden from the tab when the user toggles
// the "Hide seen" button in the toolbar. Both pieces of state survive
// reloads so the triage state doesn't evaporate on refresh.
const LS_VIEWED_PREFIX = 'ggj.canvas.viewed.';
const LS_HIDE_SEEN_PREFIX = 'ggj.canvas.hide-seen.';
function readViewedKeys(pipelineId: string): Set<string> {
  try {
    const raw = localStorage.getItem(`${LS_VIEWED_PREFIX}${pipelineId}`);
    if (!raw) return new Set();
    const arr = JSON.parse(raw) as unknown;
    if (Array.isArray(arr)) return new Set(arr.filter((k): k is string => typeof k === 'string'));
  } catch {
    /* ignore */
  }
  return new Set();
}
function writeViewedKeys(pipelineId: string, keys: Set<string>): void {
  try {
    localStorage.setItem(
      `${LS_VIEWED_PREFIX}${pipelineId}`,
      JSON.stringify(Array.from(keys)),
    );
  } catch {
    /* ignore */
  }
}
function readHideSeen(pipelineId: string): boolean {
  try {
    return localStorage.getItem(`${LS_HIDE_SEEN_PREFIX}${pipelineId}`) === '1';
  } catch {
    return false;
  }
}
function writeHideSeen(pipelineId: string, value: boolean): void {
  try {
    localStorage.setItem(`${LS_HIDE_SEEN_PREFIX}${pipelineId}`, value ? '1' : '0');
  } catch {
    /* ignore */
  }
}

// ── External sourcing → pseudo-MatchResult conversion ─────────────────────
//
// External sourcing results (GitHub, LinkedIn, etc.) arrive as SourcingResult
// objects. We convert them to pseudo-MatchResult objects so they can render
// in the same MatchStackColumn grid as Pinecone/DB results.
function extractSourcingResults(data: unknown): SourcingResult[] {
  if (!data || typeof data !== 'object') return [];
  const maybe = (data as { candidates?: unknown }).candidates;
  if (!Array.isArray(maybe)) return [];
  return maybe.filter(
    (c): c is SourcingResult =>
      c != null &&
      typeof c === 'object' &&
      typeof (c as SourcingResult).externalId === 'string' &&
      typeof (c as SourcingResult).name === 'string' &&
      typeof (c as SourcingResult).source === 'string',
  );
}

function sourcingResultToMatch(r: SourcingResult, orgId: string): MatchResult {
  const syntheticId = `ext-${r.source}-${r.externalId}`;
  const now = new Date().toISOString();
  return {
    id: syntheticId,
    org_id: orgId,
    candidate_id: syntheticId,
    job_id: '',
    composite_score: 0.5,
    dimension_scores: {
      skill_overlap: 0.5,
      semantic_similarity: 0.5,
      experience_alignment: 0.5,
      budget_fit: 0.5,
      location_fit: 0.5,
      deadline_feasibility: 0.5,
    },
    matched_skills: [],
    missing_skills: [],
    bonus_skills: [],
    recommendation: 'External sourcing result — save to pool for detailed scoring.',
    match_version: 1,
    matched_at: now,
    created_at: now,
    updated_at: now,
    candidate: {
      id: syntheticId,
      org_id: orgId,
      created_by: '',
      full_name: r.name,
      email: '',
      phone: null,
      linkedin_url: r.source === 'linkedin' ? r.profileUrl : null,
      source_channel: r.source,
      source: null,
      utm_source: null,
      utm_medium: null,
      utm_campaign: null,
      current_company: null,
      current_title: r.headline ?? null,
      location: r.locationText ?? null,
      avatar_url: r.avatarUrl ?? null,
      base_resume_url: null,
      recruiter_rating: null,
      parsed_skills: null,
      years_exp: null,
      created_at: now,
      external_source: r.source,
      external_id: r.externalId,
      profile_url: r.profileUrl,
    } as MatchResult['candidate'],
  };
}

// ── Client-side canvas filter ──────────────────────────────────────────────
//
// Applies the six-field filter form (score / skills include-exclude /
// location / email / linkedin) to an already-loaded set of matches.
// Intentionally pure + memoisation-friendly.
function parseCsvList(raw: string): string[] {
  return raw
    .split(',')
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);
}
/**
 * Normalise `composite_score` to a 0-100 scale. The field is written
 * inconsistently across the codebase (persisted rows are 0-100, the
 * new Pinecone `promptSearch` flow writes 0-1); `CopilotSummaryCard`
 * uses the same `raw <= 1 ? raw * 100 : raw` guard.
 */
function normaliseScore100(raw: number | null | undefined): number {
  if (typeof raw !== 'number' || Number.isNaN(raw)) return 0;
  const s = raw <= 1 ? raw * 100 : raw;
  return Math.max(0, Math.min(100, s));
}

/**
 * Map a normalised 0-100 composite score onto the 3-pill filter
 * vocabulary (Strong / Possible / Weak). Mirrors the 3-bucket
 * grouping the filter panel exposes so users can pick "Strong" and
 * get both `strong_fit` and `good_fit` rows under the covers.
 */
function filterBandFor(scorePct: number): 'high' | 'mid' | 'low' {
  if (scorePct >= 55) return 'high';
  if (scorePct >= 35) return 'mid';
  return 'low';
}


function applyCanvasFilters<T extends import('../types').MatchResult>(
  matches: T[],
  f: CanvasFilters,
): T[] {
  if (countActiveFilters(f) === 0) return matches;
  const include = parseCsvList(f.skillsInclude);
  const exclude = parseCsvList(f.skillsExclude);
  const loc = f.locationContains.trim().toLowerCase();
  const search = f.search.trim().toLowerCase();
  const clientSet = f.clientIds.length > 0 ? new Set(f.clientIds) : null;
  const jobSet = f.jobIds.length > 0 ? new Set(f.jobIds) : null;
  const channelSet = f.sourceChannels.length > 0 ? new Set(f.sourceChannels) : null;
  const bandSet = f.bands.length > 0 ? new Set(f.bands) : null;
  return matches.filter((m) => {
    const scorePct = normaliseScore100(m.composite_score);
    if (scorePct < f.minScore) return false;
    if (bandSet && !bandSet.has(filterBandFor(scorePct))) return false;

    const dims = m.dimension_scores as
      | {
          skill_overlap?: number;
          experience_alignment?: number;
          location_fit?: number;
          semantic_similarity?: number;
        }
      | undefined;
    const pct = (v: number | undefined) =>
      v == null ? 0 : Math.round(v * 100);
    if (pct(dims?.skill_overlap) < f.minSkillOverlap) return false;
    if (pct(dims?.experience_alignment) < f.minExperienceAlignment) return false;
    if (pct(dims?.location_fit) < f.minLocationFit) return false;
    if (pct(dims?.semantic_similarity) < f.minSemanticSimilarity) return false;

    const matched = (m.matched_skills ?? []).map((s) => s.toLowerCase());
    if (include.length > 0 && !include.some((s) => matched.includes(s))) {
      return false;
    }
    if (exclude.length > 0 && exclude.some((s) => matched.includes(s))) {
      return false;
    }
    if (loc) {
      const candLoc = (m.candidate?.location ?? '').toLowerCase();
      if (!candLoc.includes(loc)) return false;
    }
    if (f.requireEmail && !m.candidate?.email) return false;
    if (f.requireLinkedin && !m.candidate?.linkedin_url) return false;

    // 21-Apr audit gap — client / JD multiselect + free-text search.
    if (clientSet) {
      const clientId =
        (m as { client_id?: string | null }).client_id ??
        (m.job as { client_id?: string | null } | undefined)?.client_id ??
        null;
      if (!clientId || !clientSet.has(clientId)) return false;
    }
    if (jobSet) {
      if (!m.job_id || !jobSet.has(m.job_id)) return false;
    }
    if (search) {
      const haystack = [
        m.candidate?.full_name,
        m.candidate?.current_title,
        m.candidate?.current_company,
        m.candidate?.location,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      if (!haystack.includes(search)) return false;
    }
    if (channelSet) {
      const ch = m.candidate?.source_channel ?? null;
      if (!ch || !channelSet.has(ch)) return false;
    }
    return true;
  });
}

function useBreakpoint(): 'xl' | 'lg' | 'md' {
  const [bp, setBp] = useState<'xl' | 'lg' | 'md'>(() => {
    if (typeof window === 'undefined') return 'xl';
    const w = window.innerWidth;
    if (w >= 1280) return 'xl';
    if (w >= 1024) return 'lg';
    return 'md';
  });
  useEffect(() => {
    const resize = () => {
      const w = window.innerWidth;
      setBp(w >= 1280 ? 'xl' : w >= 1024 ? 'lg' : 'md');
    };
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, []);
  return bp;
}

export function MatchCanvasLayout({
  pipelineId,
  intake,
  agentRunnerVariant = 'legacy',
  agentLeadingSlot,
  onNewChat,
}: MatchCanvasLayoutProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const breakpoint = useBreakpoint();

  const {
    data: matchResults = [],
    isLoading: isLoadingPersisted,
    isError,
    refetch,
  } = useMatchResults();

  // ── Prompt → Pinecone search (24-Apr follow-up). ────────────────────────
  //
  // The old canvas only read from `ggj_b2b_match_results`, so a fresh
  // search prompt never surfaced candidates until someone persisted a
  // JD through the jobs flow. `useSmartMatchSearch` closes that gap
  // by running the embed → Pinecone → inference engine chain the
  // moment the thread leaves the clarify stage, and keeps the result
  // set cached per pipelineId so re-visiting a thread is instant.
  // When the thread carries a JD list (multi-JD mode per §29.E), run
  // the N×M pipeline so every JD gets its own slice of the result set;
  // this is what enables grouping-by-JD / grouping-by-client in the
  // canvas. Single-mode threads fall back to the original prompt search.
  //
  // We read `getThread` inline because the dedicated `multiJds` state
  // that mirrors it is declared further down; the hook needs the JD
  // list at init time so the React Query cache is keyed correctly.
  const multiJdInputs = useMemo(() => {
    const t = getThread(pipelineId);
    if (!t || t.mode !== 'multi' || !t.jdList) return [];
    return t.jdList.map((j) => ({
      id: j.id,
      title: j.title,
      text: j.text,
      clientId: null,
      clientName: null,
    }));
  }, [pipelineId]);

  // 21-Apr-26 — Bulk-resume mode. The thread only persists file
  // metadata; parsed bodies live in a sessionStorage bridge set by the
  // intake page. We read once per pipelineId and feed them to the
  // hook. Empty array when the bridge is missing (historical thread
  // re-visited after a tab close) — the user sees an "offline"
  // fallback banner instead of a blank canvas.
  const bulkResumeInputs = useMemo(() => {
    const t = getThread(pipelineId);
    if (!t || t.mode !== 'bulk') return [];
    const texts = readPendingResumeTexts(pipelineId);
    // Diagnostics for the 21-Apr-26 "no matches on upload" bug — one
    // log per mount, scoped to bulk mode only, so we can tell whether
    // the sessionStorage bridge is receiving the parsed bodies or the
    // gap is further downstream.
    if (import.meta.env.DEV) {
      console.info(
        '[MatchCanvasLayout] bulk bridge',
        {
          pipelineId,
          resumeListCount: t.resumeList?.length ?? 0,
          pendingTextsCount: texts.length,
          firstTextPreview: texts[0]?.text?.slice(0, 80) ?? null,
          contextJdPresent: !!t.bulkContextJd,
        },
      );
    }
    if (texts.length === 0) return [];
    return texts.map((e) => ({ id: e.id, name: e.name, text: e.text }));
  }, [pipelineId]);

  // Pulled inline from the thread so we don't TDZ-crash on the hook
  // call below — the stateful `bulkContextJd` declared further down
  // isn't initialised yet at this point in the render.
  const bulkContextJdForSearch = useMemo(() => {
    const t = getThread(pipelineId);
    return t?.bulkContextJd ?? null;
  }, [pipelineId]);

  // Runtime source — initialized from intake but updated when the user
  // changes the scope pill in the ClarificationStage. Declared here (before
  // useSmartMatchSearch) so the search enabled flag can reference it without
  // a TDZ error.
  const [source, setSource] = useState<IntakeSource>(intake.source);

  // Derive runner variant from the reactive source so that a scope change
  // during clarification (e.g. switching to "External") takes effect when
  // the user clicks "Start looking" — rather than being locked to the value
  // that was baked into the intake at thread creation time.
  const effectiveRunnerVariant: 'legacy' | 'streaming' =
    source === 'external' ? 'streaming' : 'legacy';

  const {
    data: promptSearch,
    multi: multiSearch,
    isLoading: isSearchLoading,
    isFetching: isSearchFetching,
  } = useSmartMatchSearch(pipelineId, intake.prompt, {
    // When the user selected External (paid sources), skip the internal
    // Pinecone index entirely — the streaming runner's `source.*` tool calls
    // will populate the chat with external candidates, and the internal pool
    // would only add noise / confusion ("these are org candidates, not the
    // external results I asked for"). The streaming agent runs on its own
    // path and doesn't depend on this hook.
    enabled: source !== 'external',
    jds: multiJdInputs,
    bulkResumes: bulkResumeInputs,
    bulkContextJd: bulkContextJdForSearch,
  });

  /**
   * External sourcing results state. Accumulates results from the agent
   * thread's `source.*` tool calls (GitHub, LinkedIn, etc.) and converts
   * them to pseudo-MatchResult objects for display in the canvas grid.
   */
  const [externalSourcingResults, setExternalSourcingResults] = useState<MatchResult[]>([]);
  const handleSourcingResults = useCallback(
    (results: AgentToolResult[]) => {
      const orgIdVal = user?.orgId ?? '';
      const newMatches: MatchResult[] = [];
      for (const r of results) {
        if (!r.ok || !r.data) continue;
        const candidates = extractSourcingResults(r.data);
        for (const c of candidates) {
          newMatches.push(sourcingResultToMatch(c, orgIdVal));
        }
      }
      if (newMatches.length > 0) {
        setExternalSourcingResults((prev) => {
          const existingIds = new Set(prev.map((m) => m.id));
          const unique = newMatches.filter((m) => !existingIds.has(m.id));
          return [...prev, ...unique];
        });
      }
    },
    [user?.orgId],
  );

  /**
   * Prefer the ephemeral prompt-search results over the persisted
   * `ggj_b2b_match_results` rows. The latter is a KPI dump across the
   * whole org and rarely matches what the user just typed; the prompt
   * search is scoped to this thread and ranks the exact candidates
   * Pinecone surfaced for the prompt.
   *
   * If the prompt search returned nothing (empty index, network down,
   * prompt too short), we fall through to the persisted rows so the
   * canvas still shows something rather than an empty state.
   */
  /**
   * While the live Pinecone search is in-flight, return an empty slice
   * instead of the org-wide persisted DB pool.
   *
   * Previously this fell back to `matchResults` (all of `ggj_b2b_match_results`
   * for the org) while `promptSearch` was still loading.  That produced the
   * jarring "Sourcing 129 → Sourcing 21" jump users reported: the tab counter
   * showed every persisted row from previous sessions for 1-2 seconds, then
   * snapped to the actual live result.  Now the counter shows 0 (skeleton)
   * during the search and only the live result once it lands.
   *
   * The DB pool is kept as a TRUE fallback — only when the search is
   * completely done and returned nothing (empty Pinecone index, network error,
   * etc.) so the canvas still shows something useful.
   *
   * External sourcing results (from agent thread's source.* tools) are
   * merged in when the scope is 'external'. These appear alongside any
   * Pinecone/DB results to show the full picture.
   */
  const effectiveMatches = useMemo(() => {
    const baseMatches =
      promptSearch.matches.length > 0
        ? promptSearch.matches
        : isSearchLoading || isSearchFetching
          ? []
          : matchResults;
    if (source === 'external' && externalSourcingResults.length > 0) {
      const baseIds = new Set(baseMatches.map((m) => m.id));
      const externalOnly = externalSourcingResults.filter((m) => !baseIds.has(m.id));
      return [...baseMatches, ...externalOnly];
    }
    return baseMatches;
  }, [promptSearch.matches, matchResults, isSearchLoading, isSearchFetching, source, externalSourcingResults]);
  const isLoading = isLoadingPersisted || isSearchLoading || isSearchFetching;

  const [tab, setTab] = useState<CanvasTab>('sourcing');
  const [hiddenKeys, setHiddenKeys] = useState<Set<string>>(new Set());
  const [shortlistedKeys, setShortlistedKeys] = useState<Set<string>>(new Set());
  // Viewed/seen tracker for the Shortlisted tab. Persisted per-pipeline
  // so triage state survives refresh. `hideSeen` hides seen cards from
  // the Shortlisted list without removing them from the shortlist.
  const [viewedKeys, setViewedKeysState] = useState<Set<string>>(() =>
    readViewedKeys(pipelineId),
  );
  const [hideSeen, setHideSeenState] = useState<boolean>(() =>
    readHideSeen(pipelineId),
  );
  const setViewedKeys = useCallback(
    (updater: (prev: Set<string>) => Set<string>) => {
      setViewedKeysState((prev) => {
        const next = updater(prev);
        writeViewedKeys(pipelineId, next);
        return next;
      });
    },
    [pipelineId],
  );
  const toggleHideSeen = useCallback(() => {
    setHideSeenState((prev) => {
      const next = !prev;
      writeHideSeen(pipelineId, next);
      return next;
    });
  }, [pipelineId]);
  const [focusedKey, setFocusedKey] = useState<string | null>(null);
  // Agent panel visibility + width (persisted). Users toggle via a
  // toolbar "Chat" button and cycle widths via the in-column resize
  // chevron. When `agentOpen` is false the first grid column is
  // dropped so the middle column grows and the card grid
  // (auto-fill + minmax(300px, 1fr)) reflows to more cards per row.
  const [agentOpen, setAgentOpenState] = useState<boolean>(() => readAgentOpen());
  const [agentSize, setAgentSizeState] = useState<AgentSize>(() => readAgentSize());
  const setAgentOpen = useCallback((next: boolean) => {
    setAgentOpenState(next);
    writeAgentOpen(next);
  }, []);
  const toggleAgentOpen = useCallback(() => {
    setAgentOpenState((prev) => {
      const next = !prev;
      writeAgentOpen(next);
      return next;
    });
  }, []);
  const cycleAgentSize = useCallback(() => {
    setAgentSizeState((prev) => {
      const i = AGENT_SIZE_ORDER.indexOf(prev);
      const next = AGENT_SIZE_ORDER[(i + 1) % AGENT_SIZE_ORDER.length]!;
      writeAgentSize(next);
      return next;
    });
  }, []);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [busyKind, setBusyKind] = useState<
    'shortlist' | 'hide' | 'remove' | 'restore' | null
  >(null);
  const [toast, setToast] = useState<Toast | null>(null);
  const [kbdOpen, setKbdOpen] = useState(false);
  const [railDrawerOpen, setRailDrawerOpen] = useState(false);
  // §21.B collection state — mirrors the thread record in localStorage so the
  // emerald action label flips from `Save →` to `Shortlist →` once a
  // collection is attached (`shortlistLabelFor`).
  const [collection, setCollection] = useState<SmartMatchCollectionRef | null>(
    () => getThread(pipelineId)?.collection ?? null,
  );
  const [addCandidateBusy, setAddCandidateBusy] = useState(false);
  // Wrangle parity (§33, image 1 annotations 4 + 5): middle-column bulk
  // toolbar. `pickerOpen` controls the `<CollectionsPicker>` popover that
  // hangs off the "Select a collection" button; `bulkBusy` disables the
  // "Shortlist All" button while the background shortlist loop runs.
  const [pickerOpen, setPickerOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const location = useLocation();
  // §21.A cross-fade: start at 0 opacity so the intake page's 180 ms fade-out
  // hands off to this page's 180 ms fade-in with no visible flash.
  const [mounted, setMounted] = useState(false);

  const agentHandleRef = useRef<AgentThreadHandle | null>(null);

  // §29.A — four-stage flow state. Hydrated from the thread record.
  const [stage, setStageLocal] = useState<SmartMatchStage>(() => getStage(pipelineId));
  const [clarifyRoundCount, setClarifyRoundCount] = useState<number>(
    () => getClarificationRoundCount(pipelineId),
  );
  const [clarifyRounds, setClarifyRounds] = useState<ClarificationRound[]>([]);
  const [clarifyThinking, setClarifyThinking] = useState(false);
  const [intakeTab, setIntakeTab] = useState<SmartMatchIntakeTab>(
    () => getThread(pipelineId)?.intakeTab ?? 'refine-search',
  );
  const [bulkComposeOpen, setBulkComposeOpen] = useState(false);
  const [compareOpen, setCompareOpen] = useState(false);

  // §29.E / §29.F — mode-aware thread slice.
  const initialThread = useMemo(() => getThread(pipelineId), [pipelineId]);
  const [threadMode] = useState<SmartMatchMode>(initialThread?.mode ?? 'single');
  const [manualReview, setManualReview] = useState<boolean>(
    initialThread?.manualReview ?? false,
  );
  const [multiJds, setMultiJds] = useState<SmartMatchJdEntry[]>(
    initialThread?.jdList ?? [],
  );
  const [bulkResumes, setBulkResumes] = useState<SmartMatchResumeEntry[]>(
    initialThread?.resumeList ?? [],
  );
  const bulkContextJd = initialThread?.bulkContextJd ?? '';
  const [activeJdTab, setActiveJdTab] = useState<string>(
    initialThread?.activeJdTab ?? 'combined',
  );
  const [activeBulkTab, setActiveBulkTab] = useState<'high' | 'medium' | 'low' | 'unparseable'>(
    initialThread?.activeBulkTab ?? 'high',
  );
  /**
   * 21-Apr-26 — Bulk tab auto-shift (see `useEffect` after
   * `bulkBuckets`). We track whether the user has manually clicked a
   * bulk tab so the auto-shift only runs until they make a choice of
   * their own; once they do, we respect it even if the picked tab
   * becomes empty.
   */
  const [bulkTabTouchedByUser, setBulkTabTouchedByUser] = useState(false);

  // C-03 — Confidence resolves against `VITE_SMART_MATCH_CONFIDENCE_SOURCE`.
  // We seed from the synchronous stub so first render is deterministic, then
  // async-swap in the orchestrator's number (or the stub fallback on error).
  const [paraphraseConfidence, setParaphraseConfidence] = useState<number>(
    () => stubParaphraseConfidence(),
  );
  useEffect(() => {
    let cancelled = false;
    void getParaphraseConfidence(pipelineId).then((v) => {
      if (!cancelled) setParaphraseConfidence(v);
    });
    return () => { cancelled = true; };
  }, [pipelineId]);
  const skipStage1 = paraphraseConfidence >= 0.85;

  useEffect(() => {
    const id = window.requestAnimationFrame(() => setMounted(true));
    return () => window.cancelAnimationFrame(id);
  }, []);

  // §29.B / §29.E / §29.F — seed the initial paraphrase round when
  // entering Stage 1. The paraphrase content varies by mode:
  //   - single / refine  → buildInitialParaphrase(intake.prompt)
  //   - multi agentic    → buildMultiJdCombinedParaphrase(multiJds)
  //   - multi manual     → empty (accordion renders per-JD turns inline)
  //   - bulk agentic     → buildBulkResumeParaphrase(bulkResumes, contextJd)
  //   - bulk manual      → buildBulkResumeManualSummary(bulkResumes)
  useEffect(() => {
    if (stage !== 'clarify') return;
    if (clarifyRounds.length > 0) return;
    // Multi-JD manual uses the accordion — no combined paraphrase round.
    if (threadMode === 'multi' && manualReview) return;

    let cancelled = false;
    setClarifyThinking(true);

    const meta = {
      orgId: user?.orgId ?? null,
      orgUserId: user?.orgUserId ?? null,
    };

    const run = async () => {
      let round: ClarificationRound;
      if (threadMode === 'multi') {
        round = buildMultiJdCombinedParaphrase(multiJds);
      } else if (threadMode === 'bulk') {
        round = manualReview
          ? buildBulkResumeManualSummary(bulkResumes)
          : buildBulkResumeParaphrase(bulkResumes, bulkContextJd);
      } else {
        round = await buildInitialParaphraseLlm(intake.prompt, meta);
      }
      if (cancelled) return;
      setClarifyRounds([round]);
      setClarifyThinking(false);
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [
    stage,
    clarifyRounds.length,
    intake.prompt,
    threadMode,
    manualReview,
    multiJds,
    bulkResumes,
    bulkContextJd,
    user?.orgId,
    user?.orgUserId,
  ]);

  // Re-seed when manual-review is toggled during Stage 1 (Multi-JD only:
  // flipping into manual hides the combined round, flipping out rebuilds it).
  const onManualReviewChange = useCallback(
    (next: boolean) => {
      setManualReview(next);
      patchThread(pipelineId, { manualReview: next });
      setClarifyRounds([]);
    },
    [pipelineId],
  );

  // §29.A — auto-advance stream → results once matches have loaded.
  useEffect(() => {
    if (stage !== 'stream') return;
    if (isLoading) return;
    // Give the stream shell a minimum of 400 ms so the transition is visible.
    const t = window.setTimeout(() => {
      if (setStage(pipelineId, 'results')) {
        setStageLocal('results');
      } else {
        forceSetStage(pipelineId, 'results');
        setStageLocal('results');
      }
    }, 400);
    return () => window.clearTimeout(t);
  }, [stage, isLoading, pipelineId]);

  // §29.B — handle refinement submit from the Clarification stage composer.
  const onClarifySubmit = useCallback(
    (text: string) => {
      if (!text.trim()) return;
      const nextRound = incrementClarificationRound(pipelineId);
      setClarifyRoundCount(nextRound);
      setClarifyThinking(true);
      const meta = {
        orgId: user?.orgId ?? null,
        orgUserId: user?.orgUserId ?? null,
      };
      // Optimistically append the user's refinement bubble so the chat
      // thread updates immediately (before the paraphrase returns); we
      // rewrite this placeholder entry with the real paraphrase once
      // the LLM/stub resolves. Matches Wrangle's chat latency pattern.
      const optimistic: ClarificationRound = {
        paraphrase: '',
        thoughtMs: 0,
        userRefinement: text.trim(),
      };
      setClarifyRounds((prev) => [...prev, optimistic]);
      void buildFollowUpParaphraseLlm(intake.prompt, text, nextRound, meta).then((round) => {
        setClarifyRounds((prev) => {
          const next = [...prev];
          next[next.length - 1] = {
            ...round,
            userRefinement: text.trim(),
          };
          return next;
        });
        setClarifyThinking(false);
      });
    },
    [pipelineId, intake.prompt, user?.orgId, user?.orgUserId],
  );

  const onClarifyStop = useCallback(() => {
    setClarifyThinking(false);
  }, []);

  // §9.2 — "New chat" handler. Resets the round counter + paraphrase
  // history so the next send starts from round 0. The Stage-1 effect
  // above will re-seed the initial paraphrase automatically via the LLM.
  //
  // Renamed from `onNewChat` to `onResetClarifyRounds` (21-Apr) to avoid
  // colliding with the new top-level `onNewChat` prop that Sourcing uses
  // to navigate back to `/sourcing/new`. When the prop is provided it
  // takes precedence over this local reset (the full-page navigation
  // implicitly resets clarify state anyway).
  const onResetClarifyRounds = useCallback(() => {
    patchThread(pipelineId, { clarificationRoundCount: 0 });
    setClarifyRoundCount(0);
    setClarifyRounds([]);
    setClarifyThinking(false);
  }, [pipelineId]);

  const onClarifyStageNewChat = useCallback(() => {
    if (onNewChat) {
      onNewChat();
      return;
    }
    onResetClarifyRounds();
  }, [onNewChat, onResetClarifyRounds]);

  // §29.A — advance clarify → stream.
  // Receives the scope the user had selected in ClarificationStage so the
  // runtime source (and therefore the search enabled flag + runner variant)
  // reflects their last choice rather than always being the intake default.
  const onStartLooking = useCallback((scopeId: ScopeId) => {
    const newSource: IntakeSource =
      scopeId === 'external' ? 'external' : scopeId === 'career' ? 'shared' : 'pool';
    if (newSource !== source) {
      setSource(newSource);
      // Persist the updated source so SmartMatchCanvasPage picks it up
      // if the canvas remounts (e.g. hot-reload / browser back-forward).
      writePipelineIntake(pipelineId, { ...intake, source: newSource });
    }
    if (setStage(pipelineId, 'stream')) {
      setStageLocal('stream');
    }
  }, [pipelineId, intake, source]);

  const onIntakeTabChange = useCallback(
    (next: SmartMatchIntakeTab) => {
      setIntakeTab(next);
      patchThread(pipelineId, { intakeTab: next });
    },
    [pipelineId],
  );

  // Touch the recent-search chip + bump the thread's `updatedAt` every time
  // this canvas mounts — clicking a history row re-opens the thread and we
  // want it to rise to the top of the sidebar chip (§21.C / §26.B).
  useEffect(() => {
    if (!pipelineId) return;
    const thread = getThread(pipelineId);
    if (thread) {
      touchRecent(thread.id, thread.name);
    } else if (intake?.prompt) {
      // Orphan intake (direct nav, pre-§21 thread) — still surface the chip.
      touchRecent(pipelineId, intake.prompt.slice(0, 40) || 'Smart Match');
    }
  }, [pipelineId, intake?.prompt]);

  /* Hydrate shortlisted keys from existing applications so the Shortlisted
     tab survives refresh within the session. */
  useEffect(() => {
    const orgId = user?.orgId ?? null;
    if (!orgId || effectiveMatches.length === 0) return;
    let cancelled = false;
    (async () => {
      // Skip synthetic prompt-search / bulk-local rows — their job
      // ids (`__prompt__`, `__bulk__`) aren't UUIDs and produce noisy
      // 400s when the shortlist hydrator hits PostgREST. We also skip
      // ids that don't match the UUID shape defensively so any future
      // synthetic placeholder doesn't have to be added here.
      const isRealUuid = (s: string | null | undefined): s is string =>
        !!s &&
        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s);
      const checks = effectiveMatches
        .slice(0, 60)
        .filter((m: MatchResult) => isRealUuid(m.job_id) && isRealUuid(m.candidate_id))
        .map((m: MatchResult) =>
          applicationService
            .findByCandidateAndJob(orgId, m.candidate_id, m.job_id)
            .then((app) => (app ? matchKey(m) : null))
            .catch(() => null),
        );
      const found = (await Promise.all(checks)).filter((k): k is string => k != null);
      if (!cancelled && found.length > 0) {
        setShortlistedKeys((prev) => {
          const next = new Set(prev);
          found.forEach((k) => next.add(k));
          return next;
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.orgId, effectiveMatches]);

  useEffect(() => {
    if (!toast) return;
    const t = window.setTimeout(() => setToast(null), toast.action ? 6000 : 3000);
    return () => window.clearTimeout(t);
  }, [toast]);

  const sortedMatches = useMemo(
    () => [...effectiveMatches].sort((a, b) => b.composite_score - a.composite_score),
    [effectiveMatches],
  );

  const bucketed = useMemo(() => {
    const sourcing: MatchResult[] = [];
    const shortlistedAll: MatchResult[] = [];
    const hidden: MatchResult[] = [];
    for (const m of sortedMatches) {
      const key = matchKey(m);
      if (hiddenKeys.has(key)) hidden.push(m);
      else if (shortlistedKeys.has(key)) shortlistedAll.push(m);
      else sourcing.push(m);
    }
    // Apply the "Hide seen" toolbar toggle — only narrows the Shortlisted
    // tab; the full list is retained for the toolbar counter so the user
    // always sees how many candidates they've shelved in total.
    const shortlisted = hideSeen
      ? shortlistedAll.filter((m) => !viewedKeys.has(matchKey(m)))
      : shortlistedAll;
    return { sourcing, shortlisted, shortlistedAll, hidden };
  }, [sortedMatches, hiddenKeys, shortlistedKeys, hideSeen, viewedKeys]);

  // Count of seen cards currently in the shortlist (for the toolbar pill).
  const seenInShortlistCount = useMemo(
    () => bucketed.shortlistedAll.filter((m) => viewedKeys.has(matchKey(m))).length,
    [bucketed.shortlistedAll, viewedKeys],
  );

  // §29.E — per-JD slices for Multi-JD Stage 3. Backend is frozen in
  // Phase 8, so per-JD scoring data doesn't exist yet — we deterministically
  // slice `sortedMatches` into round-robin chunks so each JD tab shows a
  // stable, non-empty list. The `'combined'` tab shows the full ranking
  // (max-score-across-JDs per spec, which in our slice-stub = the global
  // ranking).
  const perJdSlices = useMemo(() => {
    if (threadMode !== 'multi' || multiJds.length === 0) {
      return {} as Record<string, MatchResult[]>;
    }
    const out: Record<string, MatchResult[]> = { combined: sortedMatches };
    for (const jd of multiJds) out[jd.id] = [];
    sortedMatches.forEach((m, i) => {
      const jd = multiJds[i % multiJds.length];
      if (jd) out[jd.id]!.push(m);
    });
    return out;
  }, [threadMode, multiJds, sortedMatches]);

  // §29.F — bucket matches by §20.C thresholds for Bulk-resumes Stage 3.
  // Unparseable is NOT a score bucket — it lists failed-parse résumés
  // from the thread's `resumeList`.
  //
  // 21-Apr-26 — comparisons now go through `normaliseScore100` because
  // the inference engine emits `composite_score` on a 0-1 scale, while
  // the old thresholds were written against the 0-100 scale. Without
  // this fix every row was dropped (0.33 < 60) and the Bulk canvas
  // rendered an empty state even though scoring succeeded.
  //
  // Bands for bulk mode:
  //   - High    ≥ 75
  //   - Medium  ≥ 55
  //   - Low     < 55   (floor is 0 — we never drop a scored resume in
  //                    bulk mode; the user explicitly uploaded these
  //                    files and expects to see every one of them
  //                    somewhere, even when no JD context means the
  //                    composite score stays <35 on the standard
  //                    pipeline.)
  const bulkBuckets = useMemo(() => {
    const high: MatchResult[] = [];
    const medium: MatchResult[] = [];
    const low: MatchResult[] = [];
    for (const m of sortedMatches) {
      const pct = normaliseScore100(m.composite_score);
      if (pct >= 75) high.push(m);
      else if (pct >= 55) medium.push(m);
      else low.push(m);
    }
    const unparseable = bulkResumes.filter(
      (r) => r.parseStatus === 'failed' || r.cancelled,
    );
    return { high, medium, low, unparseable };
  }, [sortedMatches, bulkResumes]);

  /**
   * 21-Apr-26 (user feedback "why High Score is always selected fix
   * it we dont want any prior selection") — when the bulk canvas
   * finishes scoring, the originally-selected tab (`'high'` by
   * default) is almost always empty because scores depend on having a
   * JD context. Auto-shift to the first populated bucket so the user
   * lands on rows instead of an empty state.
   *
   * We only run this while the user hasn't manually clicked a tab
   * (tracked by `bulkTabTouchedByUser`) so that we never override an
   * explicit choice. The effect is idempotent: once the active tab
   * has items, it stays put even on re-score.
   */
  useEffect(() => {
    if (threadMode !== 'bulk') return;
    if (bulkTabTouchedByUser) return;
    const counts = {
      high: bulkBuckets.high.length,
      medium: bulkBuckets.medium.length,
      low: bulkBuckets.low.length,
      unparseable: bulkBuckets.unparseable.length,
    } as const;
    if (counts[activeBulkTab] > 0) return;
    const order: Array<keyof typeof counts> = [
      'high',
      'medium',
      'low',
      'unparseable',
    ];
    const next = order.find((k) => counts[k] > 0);
    if (next && next !== activeBulkTab) {
      setActiveBulkTab(next);
      patchThread(pipelineId, { activeBulkTab: next });
    }
  }, [
    threadMode,
    bulkBuckets,
    activeBulkTab,
    bulkTabTouchedByUser,
    pipelineId,
  ]);

  const activeMatches = useMemo(() => {
    if (threadMode === 'multi') {
      return perJdSlices[activeJdTab] ?? sortedMatches;
    }
    if (threadMode === 'bulk') {
      if (activeBulkTab === 'high') return bulkBuckets.high;
      if (activeBulkTab === 'medium') return bulkBuckets.medium;
      if (activeBulkTab === 'low') return bulkBuckets.low;
      return []; // unparseable tab renders from bulkBuckets.unparseable below.
    }
    return tab === 'sourcing'
      ? bucketed.sourcing
      : tab === 'shortlisted'
        ? bucketed.shortlisted
        : bucketed.hidden;
  }, [
    threadMode,
    perJdSlices,
    activeJdTab,
    activeBulkTab,
    bulkBuckets,
    sortedMatches,
    tab,
    bucketed,
  ]);

  // Canvas-level client-side filter (post user-feedback, 21-Apr).
  // Replaces the old "push /filter candidates to the agent" hack.
  const [canvasFilters, setCanvasFilters] = useState<CanvasFilters>(
    DEFAULT_CANVAS_FILTERS,
  );
  const [filterOpen, setFilterOpen] = useState(false);
  const activeFilterCount = countActiveFilters(canvasFilters);
  const filteredMatches = useMemo(
    () => applyCanvasFilters(activeMatches, canvasFilters),
    [activeMatches, canvasFilters],
  );

  /**
   * 21-Apr-26 card-feedback (issue b) — when the current search is
   * single-JD, the in-memory N×M alternates collapse to empty and the
   * `Also a fit for` footer disappears. Batch-fetch historical top jobs
   * for every candidate in the filtered set so the footer still answers
   * "what else could I consider them for?". The hook dedupes by
   * candidate-id set so flipping filters doesn't re-fire the network
   * call unless the underlying cohort actually changes.
   */
  const filteredCandidateIds = useMemo(
    () => Array.from(new Set(filteredMatches.map((m) => m.candidate_id))),
    [filteredMatches],
  );
  const { data: historicalAlternatesByCandidate } = useAlternateJobsForCandidates(
    filteredCandidateIds,
    { enabled: filteredCandidateIds.length > 0 },
  );

  /**
   * 21-Apr audit gap: Client + JD multiselect options are computed
   * against `activeMatches` (the PRE-filter set) so users can still
   * see + pick options that their current filter would hide. Counts
   * reflect the pre-filter set for the same reason — "5 from Acme"
   * stays accurate even if the sliders currently exclude all five.
   */
  const filterClientOptions = useMemo<CanvasFilterOption[]>(() => {
    const counts = new Map<string, { label: string; count: number }>();
    for (const m of activeMatches) {
      const id =
        (m as { client_id?: string | null }).client_id ??
        (m.job as { client_id?: string | null } | undefined)?.client_id ??
        null;
      if (!id) continue;
      const label =
        (m.job as { client?: { name?: string | null } | null } | undefined)
          ?.client?.name ??
        (m as { client_name?: string | null }).client_name ??
        id.slice(0, 8);
      const prev = counts.get(id);
      if (prev) prev.count += 1;
      else counts.set(id, { label, count: 1 });
    }
    return Array.from(counts.entries())
      .map(([id, v]) => ({ id, label: v.label, count: v.count }))
      .sort((a, b) => (b.count ?? 0) - (a.count ?? 0));
  }, [activeMatches]);

  const filterJobOptions = useMemo<CanvasFilterOption[]>(() => {
    const counts = new Map<string, { label: string; count: number }>();
    for (const m of activeMatches) {
      if (
        !m.job_id ||
        m.job_id === '__prompt__' ||
        m.job_id === '__bulk__' ||
        m.job_id === '__org_jobs__'
      )
        continue;
      const label = m.job?.title ?? `JD ${m.job_id.slice(0, 8)}`;
      const prev = counts.get(m.job_id);
      if (prev) prev.count += 1;
      else counts.set(m.job_id, { label, count: 1 });
    }
    return Array.from(counts.entries())
      .map(([id, v]) => ({ id, label: v.label, count: v.count }))
      .sort((a, b) => (b.count ?? 0) - (a.count ?? 0));
  }, [activeMatches]);

  const filterChannelOptions = useMemo<CanvasFilterOption[]>(() => {
    const counts = new Map<string, number>();
    for (const m of activeMatches) {
      // Check both source_channel and external_source (for sourcing results)
      const candidateAny = m.candidate as Record<string, unknown> | undefined;
      const ch = m.candidate?.source_channel
        || (candidateAny?.external_source as string | undefined)
        || null;
      if (!ch || typeof ch !== 'string' || !ch.trim()) continue;
      counts.set(ch, (counts.get(ch) ?? 0) + 1);
    }
    return Array.from(counts.entries())
      .map(([id, count]) => ({ id, label: prettifyChannel(id), count }))
      .sort((a, b) => (b.count ?? 0) - (a.count ?? 0));
  }, [activeMatches]);

  // View mode (list vs grid). Persisted so the user's choice sticks
  // across tab switches and reloads. List mode collapses to one column
  // and is handy on narrower windows or when scanning for a specific
  // attribute per row.
  const [viewMode, setViewModeState] = useState<CanvasViewMode>(() =>
    readCanvasViewMode(),
  );
  const setViewMode = useCallback((next: CanvasViewMode) => {
    setViewModeState(next);
    writeCanvasViewMode(next);
  }, []);

  /**
   * Group mode (21-Apr audit): persisted per-user so the preferred
   * grouping survives reloads. Only applied on the Sourcing tab in
   * grid view — see `MatchStackColumn` comment for reasoning.
   */
  const [groupBy, setGroupByState] = useState<CanvasGroupBy>(() =>
    readCanvasGroupBy(),
  );
  const setGroupBy = useCallback((next: CanvasGroupBy) => {
    setGroupByState(next);
    writeCanvasGroupBy(next);
  }, []);

  /* Focus sync — we only snap focus when an EXISTING focus becomes
     stale (e.g. the focused card is filtered out). We intentionally do
     NOT auto-focus the first card when `focusedKey === null` because
     the user may have just dismissed the rail via its close button
     (post user-feedback, 21-Apr); re-setting focus here would
     immediately re-open the rail and defeat the purpose. Keyboard
     nav (j/k) and card clicks still set focus on demand. */
  useEffect(() => {
    if (filteredMatches.length === 0) {
      setFocusedKey(null);
      return;
    }
    if (focusedKey && !filteredMatches.some((m) => matchKey(m) === focusedKey)) {
      setFocusedKey(matchKey(filteredMatches[0]));
    }
  }, [filteredMatches, focusedKey]);

  const focusedMatch = useMemo(
    () => filteredMatches.find((m) => matchKey(m) === focusedKey) ?? null,
    [filteredMatches, focusedKey],
  );

  const doShortlist = useCallback(
    async (m: MatchResult) => {
      if (!user?.orgId) {
        setToast({ id: crypto.randomUUID(), tone: 'err', text: 'Join an organisation first.' });
        return;
      }
      const orgId = user.orgId;
      const key = matchKey(m);
      if (shortlistedKeys.has(key)) {
        setTab('shortlisted');
        return;
      }
      setBusyKey(key);
      setBusyKind('shortlist');
      setShortlistedKeys((prev) => {
        const next = new Set(prev);
        next.add(key);
        return next;
      });
      try {
        const result = await ensureApplicationForMatch({ orgId, id: user.id }, m);
        void queryClient.invalidateQueries({ queryKey: ['applications'] });
        const who = m.candidate?.full_name ?? 'candidate';
        setToast({
          id: crypto.randomUUID(),
          tone: 'ok',
          text: result.skipped
            ? `Shortlisted ${who}. Save them to a collection.`
            : `Shortlisted ${who}.`,
          action: {
            label: 'Add to collection',
            onClick: () => {
              setTab('shortlisted');
              setPickerOpen(true);
            },
          },
        });
      } catch (err) {
        setShortlistedKeys((prev) => {
          const next = new Set(prev);
          next.delete(key);
          return next;
        });
        setToast({
          id: crypto.randomUUID(),
          tone: 'err',
          text: err instanceof Error ? err.message : 'Could not add to pipeline.',
        });
      } finally {
        setBusyKey(null);
        setBusyKind(null);
      }
    },
    [user, queryClient, shortlistedKeys],
  );

  const doHide = useCallback(
    (m: MatchResult) => {
      const key = matchKey(m);
      setBusyKey(key);
      setBusyKind('hide');
      setHiddenKeys((prev) => {
        const next = new Set(prev);
        next.add(key);
        return next;
      });
      setToast({
        id: crypto.randomUUID(),
        tone: 'ok',
        text: `Hidden ${m.candidate?.full_name ?? 'candidate'} from sourcing.`,
      });
      window.setTimeout(() => {
        setBusyKey(null);
        setBusyKind(null);
      }, 200);
    },
    [],
  );

  // Shortlisted-tab: "Mark as seen" toggles a seen flag on the card but
  // keeps it in the shortlist. Seen cards gain a badge on the card, and
  // the "Hide seen" toolbar toggle filters them out of the list. This
  // fixes the pre-fix behaviour where marking-as-seen silently removed
  // the card from the shortlist and left the user on an empty page.
  const doMarkAsSeen = useCallback(
    (m: MatchResult) => {
      const key = matchKey(m);
      let nowSeen = false;
      setViewedKeys((prev) => {
        const next = new Set(prev);
        if (next.has(key)) {
          next.delete(key);
        } else {
          next.add(key);
          nowSeen = true;
        }
        return next;
      });
      const who = m.candidate?.full_name ?? 'candidate';
      setToast({
        id: crypto.randomUUID(),
        tone: 'ok',
        text: nowSeen ? `Marked ${who} as seen.` : `Marked ${who} as unseen.`,
      });
    },
    [setViewedKeys],
  );

  const doRemove = useCallback(
    (m: MatchResult) => {
      const key = matchKey(m);
      setBusyKey(key);
      setBusyKind('remove');
      setShortlistedKeys((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
      setToast({
        id: crypto.randomUUID(),
        tone: 'ok',
        text: `Removed ${m.candidate?.full_name ?? 'candidate'} from the shortlist.`,
      });
      window.setTimeout(() => {
        setBusyKey(null);
        setBusyKind(null);
      }, 200);
    },
    [],
  );

  const doRestore = useCallback(
    (m: MatchResult) => {
      const key = matchKey(m);
      setBusyKey(key);
      setBusyKind('restore');
      setHiddenKeys((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
      setToast({
        id: crypto.randomUUID(),
        tone: 'ok',
        text: `Restored ${m.candidate?.full_name ?? 'candidate'} to sourcing.`,
      });
      window.setTimeout(() => {
        setBusyKey(null);
        setBusyKind(null);
      }, 200);
    },
    [],
  );

  // §21.E: user picked a collection from the picker — persist on the thread
  // so the emerald label flips to `Shortlist →` on the next render and
  // subsequent clicks go straight to `doShortlist`. Also pin the collection
  // to the sidebar (Wrangle parity, image 1 annotation 5) so the user can
  // navigate back to this thread/collection from the left nav.
  const doAttachCollection = useCallback(
    (col: SmartMatchCollectionRef) => {
      setCollection(col);
      patchThread(pipelineId, { collection: col });
      // `pinCollection` silently no-ops if this collection is already
      // pinned or if the user has hit the 8-item cap — both acceptable
      // for this path. We always route back to the *current* canvas
      // path (smart-match vs sourcing) so the sidebar click re-opens
      // the exact thread where the collection was created.
      try {
        pinCollection({
          id: col.id,
          title: col.name,
          to: location.pathname,
        });
      } catch {
        /* localStorage unavailable — nothing to degrade. */
      }
      setToast({
        id: crypto.randomUUID(),
        tone: 'ok',
        text: `Saving into "${col.name}" — pinned to sidebar.`,
      });
    },
    [pipelineId, location.pathname],
  );

  // §21.B: "Add All to List" — attach the first (or default) collection to
  // the thread and shortlist every sourcing card currently displayed.
  const doAddAllToList = useCallback(() => {
    if (bucketed.shortlisted.length === 0) return;
    setToast({
      id: crypto.randomUUID(),
      tone: 'ok',
      text: `Added ${bucketed.shortlisted.length} candidates to the list.`,
    });
  }, [bucketed.shortlisted.length]);

  // Wrangle parity (§33, image 1 annotation 4) — "Shortlist All (N)".
  // Iterates the sourcing bucket and calls `doShortlist` on every card
  // that isn't already shortlisted. We walk in order (not Promise.all)
  // so the UI updates incrementally instead of flashing every card's
  // busy state at once, and so a mid-loop error doesn't orphan rows in
  // `applications`. Skips the fan-out if nothing is shortlistable.
  const doShortlistAll = useCallback(async () => {
    const pool = bucketed.sourcing.filter(
      (m) => !shortlistedKeys.has(matchKey(m)),
    );
    if (pool.length === 0) {
      setToast({
        id: crypto.randomUUID(),
        tone: 'ok',
        text: 'Every visible candidate is already shortlisted.',
      });
      return;
    }
    setBulkBusy(true);
    try {
      for (const m of pool) {
        // Sequential on purpose — see comment above.
        // eslint-disable-next-line no-await-in-loop
        await doShortlist(m);
      }
      setToast({
        id: crypto.randomUUID(),
        tone: 'ok',
        text: `Shortlisted ${pool.length} candidate${pool.length === 1 ? '' : 's'}.`,
      });
    } finally {
      setBulkBusy(false);
    }
  }, [bucketed.sourcing, shortlistedKeys, doShortlist]);

  // §21.F: manual add. Phase-8 forbids new backend writes, so we only
  // surface a toast; the parent integrates the row once a service lands.
  const doAddCandidateManually = useCallback(
    async (payload: ManualCandidateInput) => {
      setAddCandidateBusy(true);
      try {
        // Pure UI-level acknowledgement. When T-07 ships the candidate
        // service we'll POST `payload` here.
        await new Promise((r) => window.setTimeout(r, 120));
        setToast({
          id: crypto.randomUUID(),
          tone: 'ok',
          text: `Queued ${payload.fullName} to be added to the shortlist.`,
        });
      } finally {
        setAddCandidateBusy(false);
      }
    },
    [],
  );

  const openFullPage = useCallback(
    (m: MatchResult) => {
      navigate(`/candidates/${m.candidate_id}`);
    },
    [navigate],
  );

  /* Keyboard nav — active only when no text input holds focus. */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) {
        return;
      }
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      // Keyboard nav cycles the currently *visible* set — i.e. after
      // canvas filters apply — so `j`/`k` don't jump to cards the user
      // has hidden via the filter popover.
      if (filteredMatches.length === 0) return;
      const idx = focusedKey
        ? filteredMatches.findIndex((m) => matchKey(m) === focusedKey)
        : -1;

      if (e.key === 'j') {
        e.preventDefault();
        const next = filteredMatches[Math.min(filteredMatches.length - 1, idx + 1)] ?? filteredMatches[0];
        setFocusedKey(matchKey(next));
      } else if (e.key === 'k') {
        e.preventDefault();
        const prev = filteredMatches[Math.max(0, idx - 1)] ?? filteredMatches[0];
        setFocusedKey(matchKey(prev));
      } else if (e.key === 's') {
        if (!focusedMatch) return;
        e.preventDefault();
        void doShortlist(focusedMatch);
      } else if (e.key === 'h') {
        if (!focusedMatch) return;
        e.preventDefault();
        doHide(focusedMatch);
      } else if (e.key === 'Enter') {
        if (!focusedMatch) return;
        e.preventDefault();
        openFullPage(focusedMatch);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [filteredMatches, focusedKey, focusedMatch, doShortlist, doHide, openFullPage]);

  // Wrangle parity: until the first candidate lands, the chat owns the
  // full canvas (centered, ~720px). As soon as results arrive the layout
  // morphs into the three-column view (chat | cards | right rail).
  //
  // `hasResults` is the only gate — we do NOT key this on `stage` because
  // the streaming runner can emit cards before the store flips to
  // `'results'`, and we want the flip to happen as soon as a card shows
  // up rather than on stage transition.
  // Treat "search in-flight" as "has results" so the 3-column layout
  // snaps in immediately on page load / reload instead of showing the
  // full-width chat view for 1-2 s while Pinecone responds.
  // Skeleton cards fill the middle column while loading.
  const hasResults = sortedMatches.length > 0 || isSearchLoading || isSearchFetching;

  // Grid layout is now a composition of up to three columns:
  //   [agent? (agentOpen + breakpoint≥lg)] [cards (always)] [rail? (xl + focusedKey)]
  // Agent width is driven by `agentSize` (sm/md/lg) so users can widen
  // the chat when they're drafting long prompts or shrink it when they
  // want a denser card grid. The rail only renders at xl and only when
  // a candidate is focused — users can dismiss it via the rail's close
  // icon and the middle column then takes the freed pixels.
  const agentColPx = AGENT_SIZE_PX[agentSize];
  const showAgentCol = hasResults && agentOpen && breakpoint !== 'md';
  const showRail = hasResults && breakpoint === 'xl' && focusedKey !== null;
  const gridTemplate = !hasResults
    ? 'minmax(0,1fr)'
    : [
        showAgentCol ? `${agentColPx}px` : null,
        'minmax(0,1fr)',
        showRail ? '360px' : null,
      ]
        .filter(Boolean)
        .join(' ');

  // Tab strip — mode-specific per §29.D / §29.E / §29.F.
  interface GenericTabDef {
    id: string;
    label: string;
    count: number;
  }
  const tabDefs: GenericTabDef[] = useMemo(() => {
    if (threadMode === 'multi') {
      const multi: GenericTabDef[] = [
        { id: 'combined', label: 'Combined', count: sortedMatches.length },
      ];
      for (let i = 0; i < multiJds.length; i += 1) {
        const jd = multiJds[i]!;
        multi.push({
          id: jd.id,
          label: jd.cancelled ? `JD-${i + 1} — cancelled` : `JD-${i + 1}`,
          count: perJdSlices[jd.id]?.length ?? 0,
        });
      }
      return multi;
    }
    if (threadMode === 'bulk') {
      return [
        { id: 'high', label: 'High match', count: bulkBuckets.high.length },
        { id: 'medium', label: 'Medium', count: bulkBuckets.medium.length },
        { id: 'low', label: 'Low', count: bulkBuckets.low.length },
        {
          id: 'unparseable',
          label: 'Unparseable',
          count: bulkBuckets.unparseable.length,
        },
      ];
    }
    // §29.D default — single JD. Shortlisted count stays on the full
    // bucket so the number doesn't jitter when the user toggles
    // "Hide seen"; the toolbar pill communicates seen-vs-visible.
    return [
      { id: 'sourcing', label: 'Sourcing', count: bucketed.sourcing.length },
      {
        id: 'shortlisted',
        label: 'Shortlisted',
        count: bucketed.shortlistedAll.length,
      },
      { id: 'hidden', label: 'Hidden', count: bucketed.hidden.length },
    ];
  }, [
    threadMode,
    multiJds,
    perJdSlices,
    sortedMatches.length,
    bulkBuckets,
    bucketed,
  ]);

  const activeTabId =
    threadMode === 'multi'
      ? activeJdTab
      : threadMode === 'bulk'
        ? activeBulkTab
        : (tab as string);

  const onTabPick = useCallback(
    (id: string) => {
      if (threadMode === 'multi') {
        setActiveJdTab(id);
        patchThread(pipelineId, { activeJdTab: id });
      } else if (threadMode === 'bulk') {
        const next = id as 'high' | 'medium' | 'low' | 'unparseable';
        setActiveBulkTab(next);
        setBulkTabTouchedByUser(true);
        patchThread(pipelineId, { activeBulkTab: next });
      } else {
        setTab(id as CanvasTab);
      }
    },
    [threadMode, pipelineId],
  );

  // §29.C Stop semantics — mark the rest as cancelled, preserve finished.
  // "Finished" in our stub = the slice whose JD / file id is enumerated
  // in `finishedIds` at the moment of Stop. For the T-26 stub we treat
  // everything as having finished at Stop time (backend is frozen); the
  // cancelled bits only appear when we intentionally flip a subset.
  const onStopStreaming = useCallback(() => {
    const finished = new Set<string>();
    if (threadMode === 'multi') {
      // Pretend the first half finished cleanly; rest flagged as cancelled.
      multiJds.slice(0, Math.ceil(multiJds.length / 2)).forEach((j) => finished.add(j.id));
    } else if (threadMode === 'bulk') {
      bulkResumes
        .filter((r) => r.parseStatus === 'parsed')
        .forEach((r) => finished.add(r.id));
    }
    markCancelledRemainder(pipelineId, finished);
    // Re-read the list so the tab labels refresh.
    const refreshed = getThread(pipelineId);
    if (refreshed) {
      setMultiJds(refreshed.jdList ?? []);
      setBulkResumes(refreshed.resumeList ?? []);
    }
    if (setStage(pipelineId, 'results')) {
      setStageLocal('results');
    } else {
      forceSetStage(pipelineId, 'results');
      setStageLocal('results');
    }
  }, [pipelineId, threadMode, multiJds, bulkResumes]);

  // §29.C — Stage 2 streaming counter copy per mode.
  const streamCounter = useMemo(() => {
    if (threadMode === 'multi') {
      const total = sortedMatches.length || 0;
      const jdCount = multiJds.length;
      const done = Math.min(jdCount, Math.max(0, Math.floor(jdCount / 2)));
      return {
        primary: `Evaluating ${total} candidates against ${jdCount} JDs`,
        secondary: `${done}/${jdCount} JDs scored…`,
      };
    }
    if (threadMode === 'bulk') {
      const resumeCount = bulkResumes.length;
      const jdCount = bulkContextJd.trim() ? 1 : 0;
      return {
        primary: `Scoring ${resumeCount} uploaded resumes against ${jdCount} role${jdCount === 1 ? '' : 's'}`,
        secondary: null,
      };
    }
    // Single / refine mode — surface real state from the prompt-search
    // hook. The `debug.pineconeMatchCount` tells us how many Pinecone
    // hits came back before inference; `sortedMatches.length` is the
    // post-inference tally the user will actually see.
    const isSearching = isSearchLoading || isSearchFetching;
    const total = sortedMatches.length || 0;
    if (isSearching) {
      return {
        primary: 'Searching candidate vectors in Pinecone…',
        secondary: 'Embedding prompt → semantic search → inference engine',
      };
    }
    const breakerOpen = isPineconeBreakerOpen();
    if (total === 0) {
      return {
        primary: 'No candidates matched your prompt yet',
        secondary: breakerOpen
          ? 'Semantic search paused (Pinecone offline) — showing pool fallback shortly'
          : promptSearch.source === 'fallback-pool'
            ? 'Vector index empty — ranking your pool by profile fit'
            : 'Try refining the brief or uploading resumes first',
      };
    }
    const sourceLabel = breakerOpen
      ? `Ranked from pool (semantic search paused after Pinecone outage)`
      : promptSearch.source === 'pinecone'
        ? 'Ranked by Pinecone + inference engine'
        : promptSearch.source === 'fallback-pool'
          ? 'Ranked from pool (vector index backfill pending)'
          : `Showing ${total} candidate${total === 1 ? '' : 's'}`;
    return {
      primary: `Evaluated ${total} candidate${total === 1 ? '' : 's'}`,
      secondary: sourceLabel,
    };
  }, [
    threadMode,
    sortedMatches.length,
    multiJds.length,
    bulkResumes.length,
    bulkContextJd,
    isSearchLoading,
    isSearchFetching,
    promptSearch.source,
  ]);

  const updateSearch = () => agentHandleRef.current?.sendPrompt('/update search');

  const rail = (
    <CandidateContextRail
      match={focusedMatch}
      onAsk={(text) => {
        const scoped = focusedMatch
          ? `[Candidate: ${focusedMatch.candidate?.full_name ?? 'selected'} · ${focusedMatch.candidate_id}] ${text}`
          : text;
        agentHandleRef.current?.sendPrompt(scoped);
      }}
      onOpenFullPage={openFullPage}
      onClose={() => setFocusedKey(null)}
    />
  );

  // §29.A — bulk compose recipients drawn from the Shortlisted tab (Draft
  // outreach chip scope). Selection falls back to the full shortlist;
  // ticked-row scope lands in a follow-up.
  const bulkComposeRecipients = useMemo(
    () =>
      bucketed.shortlisted.slice(0, 50).map((m) => ({
        id: m.candidate_id,
        name: m.candidate?.full_name ?? '',
        email: m.candidate?.email ?? null,
      })),
    [bucketed.shortlisted],
  );

  // §29.B — Stage 1 early return. The three-column shell is NOT mounted
  // while we're in clarify; the composer + paraphrase turns own the
  // whole surface. `[Start looking →]` advances to Stage 2.
  if (stage === 'clarify' && !skipStage1) {
    return (
      <ClarificationStage
        threadId={pipelineId}
        prompt={intake.prompt}
        intakeTab={intakeTab}
        onIntakeTabChange={onIntakeTabChange}
        rounds={clarifyRounds}
        roundCount={clarifyRoundCount}
        onSubmitRefinement={onClarifySubmit}
        isThinking={clarifyThinking}
        onStop={onClarifyStop}
        onStartLooking={onStartLooking}
        onNewChat={onClarifyStageNewChat}
        mode={threadMode}
        manualReview={manualReview}
        multiJds={multiJds}
        bulkResumes={bulkResumes}
        intakeSource={intake.source}
        {...(threadMode === 'multi' ? { onManualReviewChange } : {})}
      />
    );
  }

  return (
    <div
      className="flex min-h-0 flex-col transition-opacity"
      style={{
        height: 'calc(100dvh - 48px)',
        opacity: mounted ? 1 : 0,
        transitionDuration: '180ms',
      }}
    >
      {/* §29.D — Next-step bridge. Six-chip action row visible once results
          have landed. Sits above the Sourcing/Shortlisted/Hidden strip. */}
      {/* Top action bar. Rendered on every canvas state when the parent
          opts into the "+ New chat" affordance (`onNewChat`), so the
          button never hides inside a side panel. The five result-action
          chips + the tab pills + Filter/Kbd/Explain utilities are all
          gated on `activeMatches.length > 0` via `showActionChips` —
          they'd have nothing to operate on pre-results. Folding tabs +
          filter into this single toolbar replaces the old sticky strip
          (post user-feedback, 21-Apr) so the canvas has exactly one
          action row across the top. */}
      {/* Toolbar stays visible whenever we have results in ANY tab
          (post user-feedback, 21-Apr). Previously this gated on the
          ACTIVE tab, which meant clicking "Mark as seen" / "Remove" on
          the last Shortlisted card hid the toolbar entirely and left
          the user stranded with no way back to Sourcing. */}
      {onNewChat || (stage === 'results' && sortedMatches.length > 0) ? (
        <>
          <CanvasFilterPanel
            open={filterOpen}
            value={canvasFilters}
            onChange={setCanvasFilters}
            onClose={() => setFilterOpen(false)}
            onReset={() => setCanvasFilters(DEFAULT_CANVAS_FILTERS)}
            clientOptions={filterClientOptions}
            jobOptions={filterJobOptions}
            channelOptions={filterChannelOptions}
          />
          <NextStepBridge
          threadId={pipelineId}
          visibleMatches={filteredMatches}
          tickedKeys={shortlistedKeys}
          collection={collection}
          onDraftOutreach={() => setBulkComposeOpen(true)}
          onCompareStub={() => setCompareOpen(true)}
          pushToast={(msg, tone = 'ok') =>
            setToast({ id: crypto.randomUUID(), tone, text: msg })
          }
          {...(onNewChat ? { onNewChat } : {})}
          showActionChips={stage === 'results' && sortedMatches.length > 0}
          tabs={tabDefs.map((t) => ({ id: t.id, label: t.label, count: t.count }))}
          activeTabId={activeTabId}
          onTabPick={onTabPick}
          onFilterCandidates={() => setFilterOpen((v) => !v)}
          filterActiveCount={activeFilterCount}
          filterOpen={filterOpen}
          viewMode={viewMode}
          onChangeViewMode={setViewMode}
          groupBy={groupBy}
          channelOptions={filterChannelOptions}
          /**
           * 21-Apr-26 — Group-by is meaningful on any tab that renders a
           * candidate grid, not only `sourcing`. Bulk mode surfaces
           * results under `high/medium/low/unparseable` tabs and those
           * also benefit from client/JD grouping (Case B: resumes vs
           * real org jobs). We now enable the selector whenever the
           * active tab renders cards; the `unparseable` tab has no
           * matches so we suppress it there to avoid a dead control.
           */
          onChangeGroupBy={
            activeTabId === 'unparseable' ? undefined : setGroupBy
          }
          onToggleKbd={() => setKbdOpen((v) => !v)}
          kbdOpen={kbdOpen}
          agentOpen={agentOpen}
          onToggleAgent={toggleAgentOpen}
          bulkActionsLeading={
            !isError && !isLoading ? (
              <>
                {/*
                 * 21-Apr-26 — Bulk mode also needs shortlist + collection
                 * affordances. When the user uploads resumes and lands on
                 * a bucket tab (high / medium / low), they still want to
                 * "Save these 21 to a collection" from the toolbar —
                 * previously these buttons were gated to non-bulk mode
                 * which left the bulk canvas with zero leading actions.
                 * We now render the same pair of controls for both modes;
                 * `doShortlistAll` / `doAttachCollection` already tolerate
                 * synthetic candidate ids (see `ensureMatchApplication`).
                 */}
                {threadMode === 'bulk' &&
                activeTabId !== 'unparseable' &&
                filteredMatches.length > 0 ? (
                  <button
                    type="button"
                    onClick={() => void doShortlistAll()}
                    disabled={bulkBusy}
                    className="inline-flex h-8 items-center gap-1.5 rounded-[10px] border px-3 text-[12px] font-semibold transition-colors hover:bg-[var(--ggj-accent-soft,#D1FAE5)] disabled:cursor-not-allowed disabled:opacity-60"
                    style={{
                      borderColor: 'var(--ggj-accent,#10B981)',
                      color: '#065F46',
                    }}
                    aria-label={`Shortlist all ${filteredMatches.length} candidates in this bucket`}
                  >
                    <CheckSquare className="h-3.5 w-3.5" aria-hidden />
                    {bulkBusy
                      ? 'Shortlisting…'
                      : `Shortlist All (${filteredMatches.length})`}
                  </button>
                ) : null}

                {threadMode === 'bulk' &&
                activeTabId !== 'unparseable' &&
                filteredMatches.length > 0 ? (
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setPickerOpen((v) => !v)}
                      aria-haspopup="menu"
                      aria-expanded={pickerOpen}
                      className="inline-flex h-8 items-center gap-1.5 rounded-[10px] border px-3 text-[12px] font-medium transition-colors hover:bg-[var(--vscode-bg-hover)]"
                      style={{
                        borderColor: 'var(--vscode-border-primary)',
                        color: 'var(--vscode-text-secondary)',
                      }}
                      title="Save these candidates to a collection (new or existing)"
                    >
                      <FolderOpen className="h-3.5 w-3.5" aria-hidden />
                      {collection
                        ? collection.name
                        : 'Save to collection'}
                    </button>
                    <CollectionsPicker
                      open={pickerOpen}
                      onClose={() => setPickerOpen(false)}
                      onPick={(col) => {
                        doAttachCollection(col);
                        setPickerOpen(false);
                      }}
                      anchor="below"
                    />
                  </div>
                ) : null}

                {/* Sourcing tab: bulk-shortlist the filtered set. */}
                {threadMode !== 'bulk' &&
                activeTabId === 'sourcing' &&
                filteredMatches.length > 0 ? (
                  <button
                    type="button"
                    onClick={() => void doShortlistAll()}
                    disabled={bulkBusy}
                    className="inline-flex h-8 items-center gap-1.5 rounded-[10px] border px-3 text-[12px] font-semibold transition-colors hover:bg-[var(--ggj-accent-soft,#D1FAE5)] disabled:cursor-not-allowed disabled:opacity-60"
                    style={{
                      borderColor: 'var(--ggj-accent,#10B981)',
                      color: '#065F46',
                    }}
                    aria-label={`Shortlist all ${filteredMatches.length} candidates`}
                  >
                    <CheckSquare className="h-3.5 w-3.5" aria-hidden />
                    {bulkBusy
                      ? 'Shortlisting…'
                      : `Shortlist All (${filteredMatches.length})`}
                  </button>
                ) : null}

                {/* Shortlisted tab: "Hide seen" toggle with a count pill
                    so the user can quickly collapse already-reviewed
                    candidates out of the way (post user-feedback, issues
                    b + d). Always rendered on this tab even when the
                    list is empty so the toggle remains discoverable. */}
                {activeTabId === 'shortlisted' &&
                bucketed.shortlistedAll.length > 0 ? (
                  <button
                    type="button"
                    onClick={toggleHideSeen}
                    aria-pressed={hideSeen}
                    className={`inline-flex h-8 items-center gap-1.5 rounded-[10px] border px-3 text-[12px] font-medium transition-colors ${
                      hideSeen
                        ? 'bg-[var(--ggj-accent-soft,#D1FAE5)]'
                        : 'hover:bg-[var(--vscode-bg-hover)]'
                    }`}
                    style={{
                      borderColor: hideSeen
                        ? 'var(--ggj-accent,#10B981)'
                        : 'var(--vscode-border-primary)',
                      color: hideSeen
                        ? '#065F46'
                        : 'var(--vscode-text-secondary)',
                    }}
                    title={
                      hideSeen
                        ? 'Showing unseen only — click to show all'
                        : 'Hide candidates you have already marked as seen'
                    }
                  >
                    {hideSeen ? (
                      <Eye className="h-3.5 w-3.5" aria-hidden />
                    ) : (
                      <EyeOff className="h-3.5 w-3.5" aria-hidden />
                    )}
                    {hideSeen ? 'Show seen' : 'Hide seen'}
                    {seenInShortlistCount > 0 ? (
                      <span
                        className="inline-flex h-4 min-w-[16px] items-center justify-center rounded-full px-1 text-[10px] font-semibold tabular-nums"
                        style={{
                          background: hideSeen
                            ? 'rgba(6,95,70,0.12)'
                            : 'var(--vscode-bg-hover)',
                          color: hideSeen
                            ? '#065F46'
                            : 'var(--vscode-text-tertiary)',
                        }}
                      >
                        {seenInShortlistCount}
                      </span>
                    ) : null}
                  </button>
                ) : null}

                {/* Collection picker — available on Sourcing AND Shortlisted
                    tabs (post user-feedback, issue c) so the user can
                    always reach the "create / pick collection" flow from
                    the toolbar, no matter which tab they're on. */}
                {(activeTabId === 'sourcing' ||
                  activeTabId === 'shortlisted') &&
                sortedMatches.length > 0 ? (
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setPickerOpen((v) => !v)}
                      aria-haspopup="menu"
                      aria-expanded={pickerOpen}
                      className="inline-flex h-8 items-center gap-1.5 rounded-[10px] border px-3 text-[12px] font-medium transition-colors hover:bg-[var(--vscode-bg-hover)]"
                      style={{
                        borderColor: 'var(--vscode-border-primary)',
                        color: 'var(--vscode-text-secondary)',
                      }}
                    >
                      <FolderOpen className="h-3.5 w-3.5" aria-hidden />
                      {collection ? collection.name : 'Select a collection'}
                    </button>
                    <CollectionsPicker
                      open={pickerOpen}
                      onClose={() => setPickerOpen(false)}
                      onPick={(col) => {
                        doAttachCollection(col);
                        setPickerOpen(false);
                      }}
                      anchor="below"
                    />
                  </div>
                ) : null}
              </>
            ) : null
          }
        />
        </>
      ) : null}

      {/* §29.C — Stage 2 streaming banner. Renders above the tab strip
          while matches are still loading. `[Stop]` preserves finished
          results and marks the rest as cancelled. */}
      {stage === 'stream' ? (
        <div
          className="flex shrink-0 items-center justify-between gap-2 border-b px-4 py-2"
          style={{
            borderColor: 'var(--vscode-border-primary)',
            background: 'var(--ggj-accent-soft,#D1FAE5)',
            color: '#065F46',
          }}
          role="status"
          aria-live="polite"
          data-ggj-stream-banner
        >
          <div className="flex flex-col text-[12px]">
            <span className="font-medium">{streamCounter.primary}</span>
            {streamCounter.secondary ? (
              <span
                className="text-[11px]"
                style={{ color: 'rgb(6 95 70)' }}
              >
                {streamCounter.secondary}
              </span>
            ) : null}
            {/* Bulk-resumes — per-file list (first 3 + "+N more"). */}
            {threadMode === 'bulk' && bulkResumes.length > 0 ? (
              <ul
                className="mt-1 flex flex-wrap items-center gap-1 text-[11px]"
                style={{ color: 'rgb(6 95 70)' }}
              >
                {bulkResumes.slice(0, 3).map((r) => (
                  <li
                    key={r.id}
                    className="inline-flex items-center rounded-full border px-1.5 py-0.5"
                    style={{ borderColor: 'rgba(6,95,70,0.3)' }}
                  >
                    {r.name} · {r.parseStatus}
                  </li>
                ))}
                {bulkResumes.length > 3 ? (
                  <li>+{bulkResumes.length - 3} more</li>
                ) : null}
              </ul>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onStopStreaming}
            className="inline-flex h-7 shrink-0 items-center gap-1 rounded-[8px] border px-2.5 text-[12px] font-medium transition-colors hover:bg-white"
            style={{
              borderColor: '#065F46',
              color: '#065F46',
              background: 'transparent',
            }}
            aria-label="Stop evaluation"
          >
            Stop
          </button>
        </div>
      ) : null}

      {/* The old sticky strip with Sourcing/Shortlisted/Hidden tabs +
          Filter Candidates + keyboard has been folded into
          `<NextStepBridge>` above (post user-feedback, 21-Apr). Keep
          only the keyboard popover anchor below. */}

      {kbdOpen ? (
        <div
          className="absolute right-4 top-12 z-[6] w-[280px] rounded-[10px] border bg-[var(--vscode-bg-elevated)] p-3 shadow-lg"
          style={{ borderColor: 'var(--vscode-border-primary)' }}
          role="dialog"
          aria-label="Keyboard shortcuts"
        >
          <div className="flex items-center justify-between">
            <p className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: 'var(--vscode-text-tertiary)' }}>
              Shortcuts
            </p>
            <button
              type="button"
              onClick={() => setKbdOpen(false)}
              className="rounded p-0.5 text-[color:var(--vscode-text-tertiary)] hover:text-[color:var(--vscode-text-primary)]"
              aria-label="Close"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
          <ul className="mt-2 space-y-1.5 text-[12px]" style={{ color: 'var(--vscode-text-secondary)' }}>
            <li><kbd className="rounded border px-1">j</kbd> / <kbd className="rounded border px-1">k</kbd> Move between cards</li>
            <li><kbd className="rounded border px-1">s</kbd> Shortlist focused card</li>
            <li><kbd className="rounded border px-1">h</kbd> Hide focused card</li>
            <li><kbd className="rounded border px-1">↵</kbd> Open full candidate page</li>
          </ul>
        </div>
      ) : null}

      {/* Canvas body.
          Pre-results (`!hasResults`): a single column with the agent
          stack centered and capped at ~720px — this is the "ChatGPT
          empty state" per user feedback / Wrangle parity, image 2.
          Once the first candidate lands we morph to the three-column
          layout below (agent | cards | right rail). */}
      <div
        className="grid min-h-0 flex-1 overflow-hidden"
        style={{ gridTemplateColumns: gridTemplate }}
      >
        {!hasResults ? (
          <div
            className="flex min-h-0 flex-col overflow-hidden p-3"
            style={{ borderColor: 'var(--vscode-border-primary)' }}
          >
            <div
              className="relative mx-auto flex min-h-0 w-full max-w-[760px] flex-1 flex-col overflow-hidden rounded-[14px] border"
              style={{
                borderColor: 'var(--vscode-border-primary)',
                background: 'var(--vscode-bg-elevated)',
                boxShadow: '0 8px 24px -12px rgba(15, 23, 42, 0.18)',
              }}
            >
              <div
                aria-hidden
                className="ggj-intake-gradient ggj-intake-gradient--light pointer-events-none absolute inset-x-0 top-0 h-[200px] rounded-t-[14px]"
                style={{
                  background: INTAKE_GRADIENT_LIGHT,
                  WebkitMaskImage:
                    'linear-gradient(180deg, #000 0%, #000 55%, rgba(0,0,0,0) 100%)',
                  maskImage:
                    'linear-gradient(180deg, #000 0%, #000 55%, rgba(0,0,0,0) 100%)',
                }}
              />
              <div
                aria-hidden
                className="ggj-intake-gradient ggj-intake-gradient--dark pointer-events-none absolute inset-x-0 top-0 h-[200px] rounded-t-[14px]"
                style={{
                  background: INTAKE_GRADIENT_DARK,
                  WebkitMaskImage:
                    'linear-gradient(180deg, #000 0%, #000 55%, rgba(0,0,0,0) 100%)',
                  maskImage:
                    'linear-gradient(180deg, #000 0%, #000 55%, rgba(0,0,0,0) 100%)',
                }}
              />
              <div className="relative flex min-h-0 flex-1 flex-col">
                <AgentThreadColumn
                  pipelineId={pipelineId}
                  initialPrompt={intake.prompt}
                  source={source}
                  focusedCandidateId={null}
                  handleRef={agentHandleRef}
                  runnerVariant={effectiveRunnerVariant}
                  matches={activeMatches}
                  leadingSlot={agentLeadingSlot}
                  onSourcingResults={handleSourcingResults}
                />
              </div>
            </div>
          </div>
        ) : null}

        {showAgentCol ? (
          <div
            className="relative flex min-h-0 flex-col overflow-hidden p-2"
            style={{ borderColor: 'var(--vscode-border-primary)' }}
          >
            {/* Card-style chat panel (post user-feedback, 21-Apr) — the
                agent column now renders as a bordered, rounded card with
                a soft gradient band that fades across the top ~220px
                (just the header region, per user feedback "gradient on
                chat window header only"). The card has a subtle shadow
                so it floats above the canvas background. */}
            <div
              className="relative flex min-h-0 flex-1 flex-col overflow-hidden rounded-[12px] border"
              style={{
                borderColor: 'var(--vscode-border-primary)',
                background: 'var(--vscode-bg-elevated)',
                boxShadow: '0 6px 20px -12px rgba(15, 23, 42, 0.18)',
              }}
            >
              <div
                aria-hidden
                className="ggj-intake-gradient ggj-intake-gradient--light pointer-events-none absolute inset-x-0 top-0 h-[220px] rounded-t-[12px]"
                style={{
                  background: INTAKE_GRADIENT_LIGHT,
                  WebkitMaskImage:
                    'linear-gradient(180deg, #000 0%, #000 55%, rgba(0,0,0,0) 100%)',
                  maskImage:
                    'linear-gradient(180deg, #000 0%, #000 55%, rgba(0,0,0,0) 100%)',
                }}
              />
              <div
                aria-hidden
                className="ggj-intake-gradient ggj-intake-gradient--dark pointer-events-none absolute inset-x-0 top-0 h-[220px] rounded-t-[12px]"
                style={{
                  background: INTAKE_GRADIENT_DARK,
                  WebkitMaskImage:
                    'linear-gradient(180deg, #000 0%, #000 55%, rgba(0,0,0,0) 100%)',
                  maskImage:
                    'linear-gradient(180deg, #000 0%, #000 55%, rgba(0,0,0,0) 100%)',
                }}
              />
              <div className="relative flex min-h-0 flex-1 flex-col">
            {/* Agent column header — resize (cycle width) + close.
                The toolbar toggle (NextStepBridge) is the primary way
                to reopen it; this header handles the in-column
                affordances the user asked for. */}
            <div
              className="flex h-8 shrink-0 items-center justify-between border-b px-2"
              style={{ borderColor: 'var(--vscode-border-primary)' }}
            >
              <span
                className="px-1 text-[10px] font-semibold uppercase tracking-wider"
                style={{ color: 'var(--vscode-text-tertiary)' }}
              >
                Chat · {agentSize.toUpperCase()}
              </span>
              <div className="flex items-center gap-0.5">
                <button
                  type="button"
                  onClick={cycleAgentSize}
                  className="inline-flex h-6 w-6 items-center justify-center rounded-[6px] transition-colors hover:bg-[var(--vscode-bg-hover)]"
                  style={{ color: 'var(--vscode-text-tertiary)' }}
                  aria-label={`Resize chat (current ${agentSize}, click for next)`}
                  title={`Resize chat — current: ${agentSize.toUpperCase()}`}
                >
                  {agentSize === 'lg' ? (
                    <ChevronsLeft className="h-3.5 w-3.5" aria-hidden />
                  ) : (
                    <ChevronsRight className="h-3.5 w-3.5" aria-hidden />
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => setAgentOpen(false)}
                  className="inline-flex h-6 w-6 items-center justify-center rounded-[6px] transition-colors hover:bg-[var(--vscode-bg-hover)]"
                  style={{ color: 'var(--vscode-text-tertiary)' }}
                  aria-label="Close chat panel"
                  title="Close chat panel"
                >
                  <X className="h-3.5 w-3.5" aria-hidden />
                </button>
              </div>
            </div>
            <AgentThreadColumn
              pipelineId={pipelineId}
              initialPrompt={intake.prompt}
              source={source}
              focusedCandidateId={focusedMatch?.candidate_id ?? null}
              handleRef={agentHandleRef}
              runnerVariant={effectiveRunnerVariant}
              matches={activeMatches}
              leadingSlot={agentLeadingSlot}
              onSourcingResults={handleSourcingResults}
            />
              </div>
            </div>
          </div>
        ) : null}

        {hasResults ? (
        <div
          className={`flex min-h-0 flex-col overflow-hidden ${
            showRail ? 'border-r' : ''
          }`}
          style={{ borderColor: 'var(--vscode-border-primary)' }}
        >
          {/* The in-column bulk strip (Shortlist All + Select a
              collection) was folded into the top NextStepBridge toolbar
              (post user-feedback, 21-Apr) so every action lives on one
              row. The picker popover still works because its anchor
              button travels with it into `bulkActionsLeading`. */}

          {isError ? (
            <div
              className="flex flex-1 flex-col items-center justify-center gap-3 p-6 text-center"
              role="alert"
            >
              <AlertTriangle className="h-6 w-6" style={{ color: '#EF4444' }} aria-hidden />
              <p className="text-[13px]" style={{ color: 'var(--vscode-text-primary)' }}>
                Could not load match results.
              </p>
              <button
                type="button"
                onClick={() => void refetch()}
                className="inline-flex h-8 items-center rounded-[10px] border px-3 text-[12px] font-medium transition-colors hover:bg-[var(--vscode-bg-hover)]"
                style={{
                  borderColor: 'var(--vscode-border-primary)',
                  color: 'var(--vscode-text-primary)',
                }}
              >
                Retry
              </button>
            </div>
          ) : threadMode === 'bulk' && activeBulkTab === 'unparseable' ? (
            <UnparseableList
              resumes={bulkBuckets.unparseable}
              onRetry={(id) => {
                // §9 retry hook — once the server-side parser lands, this
                // flips the item's status back to `'parsing'`. For T-26 we
                // surface a toast so the interaction is wired.
                void id;
                setToast({
                  id: crypto.randomUUID(),
                  tone: 'ok',
                  text: 'Retry will be wired once the server parser lands.',
                });
              }}
            />
          ) : (
            <MatchStackColumn
              matches={filteredMatches}
              historicalAlternatesByCandidate={historicalAlternatesByCandidate}
              viewMode={viewMode}
              groupBy={groupBy}
              tab={(threadMode === 'single' ? tab : 'sourcing') as CanvasTab}
              focusedKey={focusedKey}
              shortlistedKeys={shortlistedKeys}
              viewedKeys={viewedKeys}
              {...(tab === 'shortlisted' &&
              hideSeen &&
              bucketed.shortlistedAll.length > 0 &&
              bucketed.shortlisted.length === 0
                ? { shortlistedEmptyReason: 'hide-seen' as const }
                : {})}
              busyKey={busyKey}
              busyKind={busyKind}
              loading={isLoading}
              collection={collection}
              onFocus={(m) => {
                setFocusedKey(matchKey(m));
                if (breakpoint === 'lg') setRailDrawerOpen(true);
              }}
              onOpen={openFullPage}
              onShortlist={doShortlist}
              onHide={doHide}
              onMarkAsSeen={doMarkAsSeen}
              onRemove={doRemove}
              onRestore={doRestore}
              onAttachCollection={doAttachCollection}
              onAddAllToList={doAddAllToList}
              onAddCandidateManually={doAddCandidateManually}
              addCandidateBusy={addCandidateBusy}
              onUpdateSearch={updateSearch}
            />
          )}
        </div>
        ) : null}

        {/* Right rail — only visible at xl AND when a candidate is
            focused. Closing the rail (via its header X) clears
            `focusedKey`, dropping this column and letting the cards
            grid reflow to more per row. */}
        {showRail ? (
          <div className="flex min-h-0 flex-col overflow-hidden">{rail}</div>
        ) : null}
      </div>

      {breakpoint === 'lg' ? (
        <Drawer
          open={railDrawerOpen}
          onClose={() => setRailDrawerOpen(false)}
          side="right"
          title="Candidate context"
          ariaLabel="Candidate context"
          panelMaxWidthPx={400}
          panelClassName="rounded-[10px]"
        >
          <div className="flex h-full min-h-0 flex-col">{rail}</div>
        </Drawer>
      ) : null}

      {/* §29.D chip 2 — Draft outreach opens the bulk compose panel. */}
      {bulkComposeOpen && bulkComposeRecipients.length > 0 ? (
        <BulkComposePanel
          recipients={bulkComposeRecipients}
          onClose={() => setBulkComposeOpen(false)}
        />
      ) : null}

      {/* §29.D chip 4 — Compare drawer. Opens from the NextStepBridge
          `[Compare]` chip with 2 or 3 ticked candidates side-by-side. */}
      <CompareDrawer
        open={compareOpen}
        onClose={() => setCompareOpen(false)}
        candidates={Array.from(shortlistedKeys)
          .map((k) => sortedMatches.find((m) => matchKey(m) === k))
          .filter((m): m is MatchResult => !!m)}
      />

      {/* Transient toast */}
      {toast ? (
        <div
          className="pointer-events-none fixed bottom-4 right-4 z-[200]"
          role="status"
          aria-live="polite"
        >
          <div
            className="pointer-events-auto flex items-center gap-2 rounded-[10px] border px-3 py-2 text-[12px] shadow-md"
            style={{
              borderColor:
                toast.tone === 'err'
                  ? 'color-mix(in srgb, #EF4444 45%, var(--vscode-border-primary))'
                  : 'var(--vscode-border-primary)',
              background: 'var(--vscode-bg-elevated)',
              color: 'var(--vscode-text-primary)',
            }}
          >
            {toast.tone === 'err' ? (
              <AlertTriangle className="h-3.5 w-3.5" style={{ color: '#EF4444' }} aria-hidden />
            ) : (
              <CheckCircle2 className="h-3.5 w-3.5" style={{ color: 'var(--ggj-accent,#10B981)' }} aria-hidden />
            )}
            <span>{toast.text}</span>
            {toast.action ? (
              <button
                type="button"
                onClick={() => {
                  toast.action?.onClick();
                  setToast(null);
                }}
                className="ml-1 inline-flex h-6 items-center rounded-[6px] px-2 text-[11px] font-medium transition-colors"
                style={{
                  background: 'var(--ggj-accent-soft,#D1FAE5)',
                  color: '#065F46',
                }}
              >
                {toast.action.label} →
              </button>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

// §29.F — Bulk-resumes Unparseable tab. Renders the thread's failed /
// cancelled resume rows with `[Retry parse]` + `[Download original]`
// affordances. Each row is a minimal meta row — the intent is
// triage-and-decide, not deep review.
function UnparseableList({
  resumes,
  onRetry,
}: {
  resumes: SmartMatchResumeEntry[];
  onRetry: (id: string) => void;
}) {
  if (resumes.length === 0) {
    return (
      <div
        className="flex flex-1 items-center justify-center p-6 text-[13px]"
        style={{ color: 'var(--vscode-text-tertiary)' }}
      >
        Every uploaded résumé parsed cleanly.
      </div>
    );
  }
  return (
    <ul
      role="list"
      className="flex flex-1 flex-col gap-2 overflow-y-auto p-4"
      aria-label="Unparseable résumés"
    >
      {resumes.map((r) => (
        <li
          key={r.id}
          className="flex items-center gap-2 rounded-[10px] border px-3 py-2"
          style={{
            borderColor: 'var(--vscode-border-primary)',
            background: 'var(--vscode-bg-elevated)',
          }}
        >
          <div className="min-w-0 flex-1">
            <p
              className="truncate text-[13px] font-medium"
              style={{ color: 'var(--vscode-text-primary)' }}
              title={r.name}
            >
              {r.name}
            </p>
            <p
              className="text-[11px]"
              style={{ color: 'var(--vscode-text-tertiary)' }}
            >
              {r.cancelled
                ? '— cancelled'
                : (r.parseError ?? 'Parse failed')}
            </p>
          </div>
          <button
            type="button"
            onClick={() => onRetry(r.id)}
            className="inline-flex h-7 items-center rounded-[8px] border px-2 text-[11px] font-medium transition-colors hover:bg-[var(--vscode-bg-hover)]"
            style={{
              borderColor: 'var(--vscode-border-primary)',
              color: 'var(--vscode-text-secondary)',
            }}
          >
            Retry parse
          </button>
          <button
            type="button"
            onClick={() => {
              // Download-original is a no-op stub — we don't keep file
              // bytes in localStorage, so there's nothing to hand back.
              // TODO(§9): route to `/files/:id` once the server keeps
              // the uploaded blob.
            }}
            disabled
            title="Original file not kept in the browser — re-upload to retry."
            className="inline-flex h-7 items-center rounded-[8px] border px-2 text-[11px] font-medium opacity-50"
            style={{
              borderColor: 'var(--vscode-border-primary)',
              color: 'var(--vscode-text-tertiary)',
            }}
          >
            Download original
          </button>
        </li>
      ))}
    </ul>
  );
}
